import {
  getClientRectWithMargin,
  reduceTransformBoundingBoxes,
} from "./bounding-box";

import type { SlideScrollAnchorPoint, SlideTransformState } from "../types";
import type { Ref } from "lit/directives/ref.js";

const SCROLL_ANCHOR_TOP_PADDING = 2;
const SCROLL_ANCHOR_BOTTOM_PADDING = 32;

export interface ScrollControllerProps {
  maxWindowHeight: number;
  minWindowHeight: number;
  slideRef: Ref<HTMLElement>;
  verticalPadding?: number;
}

export class ScrollController {
  set contentEl(content: HTMLElement | undefined) {
    this._contentEl = content;
  }
  get contentEl() {
    return this._contentEl;
  }
  get contentHeight() {
    return (this._contentEl?.offsetHeight ?? 0) + 2 * this._verticalPadding;
  }
  get isScrolledToBottom(): boolean {
    return this.translateY <= this.maxTranslateY;
  }
  get isScrolling(): boolean {
    return this.translateY < 0;
  }
  get maxTranslateY(): number {
    if (this.contentHeight + 2 * this._verticalPadding < this._minWindowHeight)
      return 0;

    return -1 * Math.max(0, this.contentHeight - this._maxWindowHeight);
  }
  set maxWindowHeight(height: number) {
    this._maxWindowHeight = height;
  }
  set minWindowHeight(height: number) {
    this._minWindowHeight = height;
  }
  get translateY(): number {
    return this._scroll * this.maxTranslateY;
  }
  private _contentEl?: HTMLElement;
  private _initAnimation = false;
  private _maxWindowHeight = 540;
  private _minWindowHeight = 540;
  private _scroll = 0;
  private _slideRef: Ref<HTMLElement>;
  private _verticalPadding: number;
  constructor({
    slideRef,
    maxWindowHeight,
    minWindowHeight,
    verticalPadding = 0,
  }: ScrollControllerProps) {
    this._slideRef = slideRef;
    this._verticalPadding = verticalPadding;
    this._maxWindowHeight = maxWindowHeight;
    this._minWindowHeight = minWindowHeight;
  }
  update({
    scrollY = 0,
    scrollPoints = undefined,
    scrollAnchorPoint = undefined,
    minimizeTitle = undefined,
  }: SlideTransformState) {
    setTimeout(() => (this._initAnimation = true), 50);
    this._scroll =
      this.getScrollValue(scrollPoints, scrollAnchorPoint) ?? scrollY;

    if (this._slideRef.value) {
      const style = `
        ${
          this._initAnimation
            ? ""
            : "--scroll-transition: 0; --title-transition: 0;"
        }
        --content-translate-y: ${this.translateY}px;
        --scrim-opacity: ${this.isScrolledToBottom ? 0 : 1};
      `;
      this._slideRef.value.setAttribute("style", style);

      if (this.isScrolling || minimizeTitle) {
        this._slideRef.value.classList.add("title-minimized");
      } else {
        this._slideRef.value.classList.remove("title-minimized");
      }
    }
  }
  private getScrollValue(
    scrollPoints: Array<string> | undefined,
    scrollAnchorPoint: SlideScrollAnchorPoint | undefined,
  ): null | number {
    if (!scrollPoints?.length) return null;
    const scrollPointEls: Array<HTMLElement> = scrollPoints
      .map((id) => this._contentEl?.querySelector(`[data-id="${id}"]`))
      .filter((el) => !!el)
      .map((el) => el as HTMLElement);

    if (!scrollPointEls?.length) return null;

    if (!this._contentEl) return null;
    const { top: contentClientTop, height: contentClientHeight } =
      this._contentEl.getBoundingClientRect();
    const {
      top: scrollTop,
      midY: scrollMid,
      bottom: scrollBottom,
    } = reduceTransformBoundingBoxes(
      scrollPointEls.map(getClientRectWithMargin),
    );
    const contentScale = this._contentEl.offsetHeight / contentClientHeight;
    let scrollElAnchorPoint;
    let scrollDestination;
    switch (scrollAnchorPoint) {
      case "bottom":
      case "bottom-left":
      case "bottom-right":
        scrollElAnchorPoint = (scrollBottom - contentClientTop) * contentScale;
        scrollDestination =
          this._maxWindowHeight -
          SCROLL_ANCHOR_BOTTOM_PADDING -
          this._verticalPadding;
        break;
      case "top":
      case "top-left":
      case "top-right":
        scrollElAnchorPoint = (scrollTop - contentClientTop) * contentScale;
        scrollDestination = SCROLL_ANCHOR_TOP_PADDING - this._verticalPadding;
        break;
      case "center":
      default:
        scrollElAnchorPoint = (scrollMid - contentClientTop) * contentScale;
        scrollDestination = this._maxWindowHeight / 2 - this._verticalPadding;
        break;
    }

    return Math.min(
      1,
      Math.max(
        0,
        (scrollElAnchorPoint - scrollDestination) /
          Math.abs(this.maxTranslateY),
      ),
    );
  }
}
